<div class="users-page-container">
  @if (!hasLoadedOnce() && isLoading() && !initialLoadFailed()) {
    <tf-settings-loading-placeholder [rows]="5" [columns]="6" />
  } @else if (initialLoadFailed() && loadError(); as error) {
    <tf-settings-load-error
      [title]="error"
      [message]="'SETTINGS.LOAD_ERROR.MESSAGE' | translate"
      [details]="loadErrorDetails()"
      [retrying]="isLoading()"
      (retry)="reloadUsers()" />
  } @else {
    <tf-settings-list-header
      [searchQuery]="searchQuery()"
      [searchPlaceholder]="'SETTINGS_ACCESS.USERS.SEARCH_PLACEHOLDER' | translate"
      [loading]="isLoading()"
      [refreshTooltip]="'SETTINGS_ACCESS.USERS.RELOAD_USERS' | translate"
      (searchChange)="onSearchChange($event)"
      (refresh)="reloadUsers()">
      <p-button
        headerStart
        icon="pi pi-filter"
        [label]="'SETTINGS_ACCESS.USERS.FILTERS' | translate"
        [severity]="activeFilterCount() > 0 ? 'primary' : 'secondary'"
        [attr.aria-label]="'SETTINGS_ACCESS.USERS.FILTERS' | translate"
        (onClick)="openFiltersDialog()" />
      <p-button
        headerActions
        [label]="'SETTINGS_ACCESS.USERS.CREATE_USER' | translate"
        icon="pi pi-plus"
        severity="primary"
        data-testid="btn-settings-users-create"
        [disabled]="!canCreateUser()"
        [pTooltip]="canCreateUser() ? '' : ('GENERAL.NO_PERMISSION' | translate)"
        tooltipPosition="top"
        (onClick)="createUser()" />
    </tf-settings-list-header>

    <tf-settings-inline-message
      [message]="actionError()"
      messageId="users-action-error"
      (dismissed)="clearActionError()" />

    @if (activeFilterChips().length > 0) {
      <div class="users-page-container__chips">
        @for (chip of activeFilterChips(); track chip.kind + ':' + chip.value) {
          <tf-simple-chip [label]="chip.label" (removed)="removeFilter(chip.kind, chip.value)" />
        }
        <p-button
          [label]="'GENERAL.CLEAR_ALL' | translate"
          severity="secondary"
          size="small"
          [text]="true"
          data-testid="btn-settings-users-clear-filters"
          (onClick)="clearAllFilters()" />
      </div>
    }

    <tf-data-table
      data-testid="cmp-settings-users-table"
      [columns]="columns()"
      [value]="editableUsers()"
      [loading]="false"
      [paginator]="true"
      [lazy]="true"
      [totalRecords]="totalRecords()"
      [rows]="pageSize()"
      [first]="first()"
      [sortField]="sortField() ?? undefined"
      [sortOrder]="sortOrder()"
      [currentPageReportTemplate]="'SETTINGS_ACCESS.USERS.PAGINATION_MESSAGE' | translate"
      styleClass="p-datatable-sm"
      emptyMessageKey="SETTINGS_ACCESS.USERS.NO_USERS_FOUND"
      (sorted)="onSort($event)"
      (paged)="onPageChange($event)"
      (actionClick)="onMenuActionClick($event)">
      <ng-template tfColumnDef="name" let-row>
        {{ row.vm.displayName }}
      </ng-template>
      <ng-template tfColumnDef="_roles" let-row>
        {{ row.vm.roleDisplay }}
      </ng-template>
      <ng-template tfColumnDef="_skills" let-row>
        <div class="skills-cell">
          @for (skill of row.vm.visibleSkills; track skill.id) {
            <span class="skill-chip">{{ skill.name }}</span>
          }
          @if (row.vm.hiddenSkillsCount > 0) {
            <span
              class="skill-overflow"
              [pTooltip]="row.vm.hiddenSkillsLabel"
              tooltipPosition="top">
              +{{ row.vm.hiddenSkillsCount }}
            </span>
          }
          @if (!row.vm.hasSkills) {
            <span class="empty-value">-</span>
          }
        </div>
      </ng-template>
      <ng-template tfColumnDef="_tags" let-row>
        <div class="tags-cell">
          @for (tag of row.vm.visibleTags; track tag.id) {
            <span class="tag-chip" [title]="tag.name">
              <span class="tag-chip__dot" [style.background-color]="tag.color"></span>
              <span class="tag-chip__label">{{ tag.name }}</span>
            </span>
          }
          @if (row.vm.hiddenTagsCount > 0) {
            <span class="tag-overflow" [pTooltip]="row.vm.hiddenTagsLabel" tooltipPosition="top">
              +{{ row.vm.hiddenTagsCount }}
            </span>
          }
          @if (!row.vm.hasTags) {
            <span class="empty-value">-</span>
          }
        </div>
      </ng-template>
    </tf-data-table>
  }

  <!-- Edit User Dialog -->
  <p-dialog
    [visible]="isEditDialogVisible()"
    (visibleChange)="isEditDialogVisible.set($event)"
    [modal]="true"
    [closable]="!isSaving()"
    [style]="{ width: '37.5rem' }"
    header="{{ 'SETTINGS_ACCESS.USERS.EDIT_USER' | translate }}"
    [draggable]="false"
    [resizable]="false">
    <tf-settings-inline-message
      [message]="actionError()"
      messageId="users-edit-error"
      (dismissed)="clearActionError()" />

    <tf-user-form
      mode="edit"
      idPrefix="editUser"
      [formGroup]="editUserForm"
      [serverErrors]="editServerErrors"
      [availableRoles]="availableRoles()"
      [availableSkills]="availableSkills()"
      [availableTagOptions]="availableTagOptions()"
      [canAssignSkills]="canAssignSkillsOnEdit()"
      [getRoleLabel]="getRoleLabelFn"
      [canAssignRole]="canAssignRoleFn" />

    <ng-template #footer>
      <p-button
        label="{{ 'GENERAL.CANCEL' | translate }}"
        severity="secondary"
        [outlined]="true"
        [disabled]="isSaving()"
        (onClick)="closeEditDialog()" />
      <p-button
        label="{{ 'GENERAL.SAVE' | translate }}"
        severity="primary"
        [loading]="isSaving()"
        [disabled]="!canSaveEditForm() || isSaving()"
        (onClick)="saveEditing()" />
    </ng-template>
  </p-dialog>

  <!-- Create User Dialog -->
  <p-dialog
    [visible]="isCreateDialogVisible()"
    (visibleChange)="isCreateDialogVisible.set($event)"
    [modal]="true"
    [closable]="!isSaving()"
    [style]="{ width: '37.5rem' }"
    header="{{ 'SETTINGS_ACCESS.USERS.CREATE_USER' | translate }}"
    data-testid="dlg-settings-users-create"
    [draggable]="false"
    [resizable]="false">
    <tf-settings-inline-message
      [message]="actionError()"
      messageId="users-create-error"
      (dismissed)="clearActionError()" />

    <tf-user-form
      mode="create"
      idPrefix="newUser"
      [formGroup]="createUserForm"
      [serverErrors]="createServerErrors"
      [availableRoles]="availableRoles()"
      [availableSkills]="availableSkills()"
      [availableTagOptions]="availableTagOptions()"
      [canAssignSkills]="canAssignSkillsOnCreate()"
      [getRoleLabel]="getRoleLabelFn"
      [canAssignRole]="canAssignRoleFn" />

    <ng-template #footer>
      <p-button
        label="{{ 'GENERAL.CANCEL' | translate }}"
        severity="secondary"
        [outlined]="true"
        [disabled]="isSaving()"
        (onClick)="closeCreateDialog()" />
      <p-button
        label="{{ 'SETTINGS_ACCESS.USERS.CREATE_USER' | translate }}"
        severity="primary"
        [loading]="isSaving()"
        [disabled]="createUserForm.invalid || isSaving()"
        (onClick)="saveNewUser()" />
    </ng-template>
  </p-dialog>

  <p-confirmDialog />
</div>
